<script setup lang="ts">
import FooterBar from '@/components/footer-bar/FooterBar.vue'
import { useUserStore } from '@/stores/modules/user'
import type { EquipItemType } from '@/types/equip'
import { computed, ref } from 'vue'
import { getEquipName } from '@/utils/equip'
import EquipInfo from '../../item/components/EquipInfo.vue'
import { QuestionFilled } from '@element-plus/icons-vue'
import { socketEmitAsync } from '@/utils/web-socket'
import type { ItemType } from '@/types/item'
import { formatNumberWithCommas } from '@/utils'
import { getDisassembleResStone } from '../data'

const userStore = useUserStore()

const itemList = computed(() => {
  return userStore.getItemEquipInfoList.filter((item) => {
    if (item.isLock || item.reinforcedLevel || item.quality !== 'supreme') return false
    if (disassembleIdList.value.includes(item.id)) return false
    if (item.refineAttr?.[0]) return true
    return false
  })
})
const disassembleIdList = ref<string[]>([])
const disassembleItemList = computed(() => {
  return userStore.getItemEquipInfoList.filter((item) => {
    return disassembleIdList.value.includes(item.id)
  })
})

const equipInfo = ref<EquipItemType>()
const isShowEquipInfo = ref(false)
async function handleAction(equipItem: EquipItemType, type: 'putIn' | 'takeOut') {
  if (isShowEquipInfo.value) return (equipInfo.value = equipItem)
  const { id } = equipItem
  if (!userStore.warehouseItemList) userStore.warehouseItemList = []
  if (type === 'putIn') {
    disassembleIdList.value.push(id)
  } else {
    const index = disassembleIdList.value.findIndex((item) => item === id)
    if (index === -1) return
    disassembleIdList.value.splice(index, 1)
  }
}

function handleClear() {
  disassembleIdList.value = []
}
function handledisassemble() {
  const supremeRefineStone = disassembleIdList.value.reduce((pre, id) => {
    const equipInfo = userStore.itemList.find((item) => item.id === id)
    if (!equipInfo) return pre
    const level = equipInfo?.refineAttr?.[0] || 0
    return (pre += getDisassembleResStone(level))
  }, 0)

  ElMessageBox.confirm(
    `<p class="affix">您确定要进行<span class="info">分解</span>吗？该操作将导致您材料台当中的装备<span class="hp">全部销毁</span>，请务必谨慎（本次分解可获得${supremeRefineStone} 🟨至臻精造石）</p>`,
    '提示',
    {
      type: 'warning',
      dangerouslyUseHTMLString: true,
      appendTo: 'body',
      confirmButtonClass: 'is-plain',
    },
  )
    .then(async () => {
      await socketEmitAsync<ItemType>({
        type: 'disassembleSupremeEquip',
        data: { idList: disassembleIdList.value },
      })
      for (const id of disassembleIdList.value) {
        const index = userStore.itemList.findIndex((item) => item.id === id)
        userStore.itemList.splice(index, 1)
      }

      disassembleIdList.value = []
      userStore.consumables.supremeRefineStone += supremeRefineStone

      ElMessage.success(`分解成功，获得${supremeRefineStone} 🟨至臻精造石`)
    })
    .catch(() => {})
}
</script>

<template>
  <div class="disassemble">
    <p class="header">
      <el-popover placement="bottom" width="100%" trigger="click">
        <template #reference>
          <el-icon size="18"><QuestionFilled /></el-icon>
        </template>
        <div class="affix">
          <h4>分解说明</h4>
          <p>
            1. 将<span class="info">已精造</span>的<span class="supreme-simple">至臻</span>装备用于分解，分解将<span class="shrink">销毁</span
            >装备，并全额返还装备所消耗的<span class="supreme-simple">至臻精造石</span>。
          </p>
          <p>2. 您无法将<span class="info">已锁定、已强化</span>的装备放入材料台。</p>
        </div>
      </el-popover>
      <span>🟨 {{ formatNumberWithCommas(userStore.consumables.supremeRefineStone) }}</span>
    </p>
    <div class="main">
      <div class="item-list">
        <div class="border-wrap">
          <h4>背包</h4>
          <div style="display: flex; align-items: center; padding: 0 5px">
            <span style="font-size: 12px">查看装备信息：</span>
            <el-switch v-model="isShowEquipInfo" size="small" @change="equipInfo = undefined" />
          </div>
          <div class="item-btn-wrap btn-wrap">
            <el-row :gutter="10">
              <el-col :span="24" v-for="item of itemList" :key="item.id">
                <CommonBtn
                  size="small"
                  @done="handleAction(item, 'putIn')"
                  :style="{ borderColor: item?.id && equipInfo?.id === item.id ? 'red' : '' }"
                >
                  <span v-html="getEquipName(item, item.type)"></span>
                </CommonBtn>
              </el-col>
            </el-row>
            <p class="affix" style="text-align: center" v-show="!itemList.length">
              您的背包中不存在<span class="info">已精造</span>且<span class="info">未锁定、未强化</span>的装备
            </p>
          </div>
        </div>
        <div class="border-wrap" style="padding: 10px; min-height: 50%; margin-top: 5px" v-show="isShowEquipInfo">
          <h4>装备信息</h4>
          <template v-if="equipInfo">
            <EquipInfo :equip-info="equipInfo" :show-action="false" :show-lock="false" />
          </template>
        </div>
      </div>
      <div class="item-list">
        <div class="action border-wrap">
          <h4>操作</h4>
          <el-button type="primary" :disabled="!Boolean(disassembleIdList.length)" plain size="small" @click="handledisassemble">分解</el-button>
          <el-button type="warning" plain size="small" @click="handleClear">清空材料台</el-button>
        </div>
        <div class="border-wrap">
          <h4>材料台</h4>
          <div class="item-btn-wrap btn-wrap">
            <el-row :gutter="10">
              <el-col :span="24" v-for="item of disassembleItemList" :key="item.id">
                <CommonBtn
                  size="small"
                  @done="handleAction(item, 'takeOut')"
                  :style="{ borderColor: item?.id && equipInfo?.id === item.id ? 'red' : '' }"
                >
                  <span v-html="getEquipName(item, item.type)"> </span>
                </CommonBtn>
              </el-col>
            </el-row>
          </div>
        </div>
      </div>
    </div>
    <FooterBar />
  </div>
</template>

<style lang="scss" scoped>
.disassemble {
  padding-top: 0;
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-flow: column nowrap;
  .header {
    display: flex;
    justify-content: space-between;
    margin: 0 0 5px 0;
    padding: 0 15px;
    font-size: 14px;
  }
  .item-filter {
    margin-bottom: 10px;
    display: flex;
    .el-button {
      margin-left: 10px;
    }
  }
  .main {
    display: flex;
    justify-content: space-between;
    overflow: hidden;
    flex: 1;
  }
  .item-list {
    width: calc(50% - 5px);
    overflow: hidden;
    display: flex;
    flex-flow: column nowrap;
    .item-btn-wrap {
      overflow: auto;
      :deep() {
        .el-button:focus,
        .el-button:hover {
          background-color: unset;
        }
      }
    }
    .border-wrap {
      overflow: hidden;
      display: flex;
      flex-flow: column nowrap;
      height: 100%;
      &.action {
        height: auto;
        margin-bottom: 10px;
        padding: 0 10px 10px 10px;
        flex-shrink: 0;
        .el-button {
          margin-bottom: 10px;
          margin-left: 0;
        }
      }
    }
  }
  .btn-wrap {
    & > div {
      justify-content: unset;
      margin-bottom: 0;
    }
    .common-btn-wrap {
      margin-bottom: 10px;
    }
  }
}
</style>
